import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, Star } from 'lucide-react';
import SEO from '../components/SEO';
import { api } from '../lib/api';
import { prerenderedPosts } from '../lib/prerender';

const formatDate = (d) =>
  new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const FeaturedCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group block bg-white rounded-3xl border border-[var(--line-2)] overflow-hidden lift"
  >
    <div className="grid md:grid-cols-5 gap-0">
      <div className="md:col-span-3 aspect-[16/10] md:aspect-auto bg-[var(--bg-alt)] flex items-center justify-center overflow-hidden">
        {post.cover_image ? (
          <img src={post.cover_image} alt={post.alt_text || post.title} loading="lazy" decoding="async" width="800" height="500" className="w-full h-full object-cover" />
        ) : (
          <div className="text-center px-10 py-12">
            <div className="text-3xl md:text-5xl font-semibold text-ink tracking-tight leading-tight">
              {post.title.split(' ').slice(0, 4).join(' ')}
            </div>
            <div className="text-sm text-ink-3 mt-3">{post.title.split(' ').slice(4).join(' ')}</div>
          </div>
        )}
      </div>
      <div className="md:col-span-2 p-8 lg:p-10 flex flex-col justify-center">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.15em] uppercase text-ink mb-4">
          <Star className="w-3 h-3 fill-current" /> Featured
        </div>
        <div className="flex flex-wrap gap-3 mb-3">
          {(post.tags || []).slice(0, 3).map((t) => (
            <span key={t} className="text-[10px] uppercase tracking-widest text-ink-3">{t}</span>
          ))}
        </div>
        <h2 className="text-2xl md:text-3xl font-semibold text-ink leading-tight tracking-tight mb-4 group-hover:underline">
          {post.title}
        </h2>
        <p className="text-ink-2 leading-relaxed mb-6 line-clamp-4">{post.excerpt}</p>
        <div className="flex items-center justify-between">
          <div className="text-[11px] text-ink-3">
            {formatDate(post.created_at)} · {post.author}
          </div>
          <span className="inline-flex items-center gap-1 text-sm text-ink font-medium">
            Read <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </div>
  </Link>
);

const PostCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group bg-white rounded-2xl border border-[var(--line-2)] overflow-hidden lift"
  >
    <div className="aspect-[16/10] bg-[var(--bg-alt)] flex items-center justify-center overflow-hidden">
      {post.cover_image ? (
        <img src={post.cover_image} alt={post.alt_text || post.title} loading="lazy" decoding="async" width="800" height="500" className="w-full h-full object-cover" />
      ) : (
        <div className="text-center px-6">
          <div className="text-3xl font-semibold text-ink tracking-tight leading-tight">
            {post.title.split(' ').slice(0, 3).join(' ')}
          </div>
        </div>
      )}
    </div>
    <div className="p-6">
      <div className="flex flex-wrap gap-2 mb-3">
        {(post.tags || []).slice(0, 2).map((t) => (
          <span key={t} className="text-[10px] uppercase tracking-widest text-ink-3">{t}</span>
        ))}
      </div>
      <h3 className="text-lg font-semibold text-ink mb-2 leading-snug group-hover:underline">
        {post.title}
      </h3>
      <p className="text-sm text-ink-2 line-clamp-3 leading-relaxed">{post.excerpt}</p>
      <div className="mt-4 text-[11px] text-ink-3">
        {formatDate(post.created_at)} · {post.author}
      </div>
    </div>
  </Link>
);

const BlogListPage = () => {
  const initial = prerenderedPosts();
  const [posts, setPosts] = useState(initial || []);
  const [loading, setLoading] = useState(!initial);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/blog');
        setPosts(res.data);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const featured = posts.find((p) => p.featured);
  const rest = posts.filter((p) => p.id !== featured?.id);

  return (
    <>
      <SEO
        title="Blog"
        description="Insights on executive briefings, corporate events, enterprise engagement, and AI — from the BriefingIQ team."
        path="/blog"
        keywords={['briefing insights', 'event insights', 'enterprise engagement blog']}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'BriefingIQ Blog',
          url: (typeof window !== 'undefined' ? window.location.origin : '') + '/blog',
          blogPost: posts.map((p) => ({
            '@type': 'BlogPosting',
            headline: p.title,
            description: p.excerpt,
            datePublished: p.created_at,
            author: { '@type': 'Person', name: p.author || 'BriefingIQ Team' },
            image: p.cover_image || undefined,
            url: (typeof window !== 'undefined' ? window.location.origin : '') + '/blog/' + p.slug,
          })),
        }}
      />

      <section className="bg-white pt-20 pb-12 lg:pt-28 lg:pb-14">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-5">Insights</div>
          <h1 className="headline-xl text-ink mb-6">Notes on engagement.</h1>
          <p className="text-lg text-ink-2 max-w-2xl leading-relaxed">
            Perspectives on briefing programs, corporate events, and how enterprises are engaging their most important stakeholders.
          </p>
        </div>
      </section>

      <section className="bg-[var(--bg-alt)] section">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-ink-3">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20 text-ink-3">No posts yet. Check back soon.</div>
          ) : (
            <>
              {featured && (
                <div className="mb-10">
                  <FeaturedCard post={featured} />
                </div>
              )}
              {rest.length > 0 && (
                <>
                  {featured && (
                    <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-6">More posts</div>
                  )}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rest.map((p) => (
                      <PostCard key={p.id} post={p} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default BlogListPage;
